import {
	AfterViewInit,
	Component,
	ElementRef,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { TxApiService } from '../../../shared/services/txapi.service';
import { Subscription } from 'rxjs';
import {
	BarController,
	BarElement,
	CategoryScale,
	Chart,
	Legend,
	LinearScale,
	LineController,
	LineElement,
	PointElement,
	Tooltip,
	ChartDataset, // falls benötigt
} from 'chart.js';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

// Register Chart.js components
Chart.register(
	BarController,
	LineController,
	BarElement,
	LineElement,
	PointElement,
	LinearScale,
	CategoryScale,
	Tooltip,
	Legend
);

interface TimeAccountSum {
	project_minutes: number;
	year: number;
	month: number;
	target_minutes: number;
	worked_minutes: number;
	difference_minutes: number;
	project_diff_minutes: number;
	bde_act: number;
	AbsenceList: Array<{
		id: number;
		name: string;
		day_count: number;
		color: string;
	}>;
}

interface AbsenceType {
	id: number;
	name: string;
	totalDays: number;
	color: string;
}

interface TimeRangeOption {
	label: string;
	value: number;
	labelKey: string;
}

// Hier erweitern wir das ChartDataset um unsere Eigenschaft "absenceId"
interface AbsenceChartDataset extends ChartDataset<'bar', number[]> {
	absenceId: number;
}

@Component({
	selector: 'tx-time-sums-chart',
	templateUrl: './tx-time-sums-chart.component.html',
	styleUrls: ['./tx-time-sums-chart.component.scss'],
})
export class TxTimeSumsChartComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
	@ViewChild('chartCanvas') chartCanvas: ElementRef;
	@ViewChild('absenceChartCanvas') absenceChartCanvas: ElementRef;

	timeRangeOptions: TimeRangeOption[] = [
		{ label: '', value: 3, labelKey: 'txZeitKonto.last3Months' },
		{ label: '', value: 6, labelKey: 'txZeitKonto.last6Months' },
		{ label: '', value: 12, labelKey: 'txZeitKonto.last12Months' },
		{ label: '', value: 15, labelKey: 'txZeitKonto.last15Months' },
		{ label: '', value: -1, labelKey: 'txZeitKonto.lastYear' },
	];

	selectedTimeRange = new FormControl(this.timeRangeOptions[1].value);
	currentTimeRangeOption: TimeRangeOption = this.timeRangeOptions[1];
	timeAccountSums: TimeAccountSum[] = [];
	absenceTypes: AbsenceType[] = [];
	loading = false;
	chart: Chart;
	absenceChart: Chart;

	// Für die Filterung im Zeit-Chart
	selectedDataset: 'target' | 'worked' | 'project' | 'difference' | 'project_diff' = null;
	// Für die Filterung im Abwesenheits-Chart speichern wir die aktuell ausgewählte Abwesenheits-ID
	selectedAbsenceDataset: number | null = null;

	private subscriptions = new Subscription();
	public showProjectChart: boolean;
	public bde_act: boolean;

	constructor(private txApiService: TxApiService, private translate: TranslateService) {}

	ngOnInit(): void {
		this.refreshTimeRangeLabels();
		this.subscriptions.add(
			this.translate.onLangChange.subscribe(() => {
				this.refreshTimeRangeLabels();
				if (this.chart) {
					this.createChart();
				}
				if (this.absenceChart) {
					this.createAbsenceChart();
				}
			})
		);

		this.txApiService.callAPI('getSetting', { settingName: 'SYS_BDE' }).subscribe((response) => {
			this.showProjectChart = response.settingValue;
		});
		this.selectedTimeRange.valueChanges.subscribe((selectedValue) => {
			this.currentTimeRangeOption =
				this.timeRangeOptions.find((option) => option.value === selectedValue) || this.timeRangeOptions[1];
			this.fetchData();
		});

		this.fetchData();
	}

	private refreshTimeRangeLabels(): void {
		this.timeRangeOptions = this.timeRangeOptions.map((option) => ({
			...option,
			label: this.translate.instant(option.labelKey),
		}));
		const current = this.timeRangeOptions.find((o) => o.value === this.selectedTimeRange.value);
		if (current) {
			this.currentTimeRangeOption = current;
		}
	}

	private getLocalizedMonthNames(): string[] {
		return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) =>
			this.translate.instant(`txZeitKonto.monthShort.${m}`)
		);
	}

	ngAfterViewInit(): void {
		if (this.timeAccountSums.length > 0) {
			this.createChart();
			this.createAbsenceChart();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['timeAccountSums'] && !changes['timeAccountSums'].firstChange) {
			if (this.chart) {
				this.updateChart();
			}
			if (this.absenceChart) {
				this.updateAbsenceChart();
			}
		}
	}

	ngOnDestroy(): void {
		if (this.chart) {
			this.chart.destroy();
		}
		if (this.absenceChart) {
			this.absenceChart.destroy();
		}
		this.subscriptions.unsubscribe();
	}

	fetchData(): void {
		this.loading = true;
		let endDate = new Date();
		let startDate = new Date();

		if (this.selectedTimeRange.value === -1) {
			// Use current year to calculate last year correctly
			const lastYear = new Date().getFullYear() - 1;
			startDate = new Date(lastYear, 0, 1);
			endDate = new Date(lastYear, 11, 31);
		} else {
			// Setze das Enddatum auf den letzten Tag des vorherigen Monats
			endDate.setDate(0); // Setzt auf den letzten Tag des Vormonats
			// Starte beim Ende des vorherigen Monats und gehe die gewählte Anzahl von Monaten zurück
			startDate = new Date(endDate);
			startDate.setMonth(endDate.getMonth() - (this.selectedTimeRange.value - 1));
			startDate.setDate(1); // Setzt auf den ersten Tag des Startmonats
		}

		const request = {
			von: this.formatDate(startDate),
			bis: this.formatDate(endDate),
		};

		this.subscriptions.add(
			this.txApiService.callAPI('getDashboardTimeAccSums', request).subscribe(
				(response) => {
					if (response && response.TimeAccountSumList) {
						this.timeAccountSums = response.TimeAccountSumList;
						this.absenceTypes = this.calculateAbsenceTypes();
						this.createChart();
						this.createAbsenceChart();
					}
					this.loading = false;
				},
				(error) => {
					console.error('Error fetching time account data:', error);
					this.loading = false;
				}
			)
		);
	}

	private formatDate(date: Date): string {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}T00:00:00`;
	}

	private getCssVariable(variableName: string): string {
		const computedStyle = getComputedStyle(this.chartCanvas.nativeElement);
		return computedStyle.getPropertyValue(variableName).trim();
	}

	private createChart(): void {
		if (!this.chartCanvas) {
			return;
		}
		if (this.chart) {
			this.chart.destroy();
		}
		const ctx = this.chartCanvas.nativeElement.getContext('2d');
		const sortedData = [...this.timeAccountSums].sort((a, b) => a.year * 100 + a.month - (b.year * 100 + b.month));
		const labels = sortedData.map((item) => {
			const monthNames = this.getLocalizedMonthNames();
			return `${monthNames[item.month - 1]} ${item.year}`;
		});

		this.bde_act = !!sortedData.map((item) => item.bde_act)[0];

		const targetHours = sortedData.map((item) => this.minutesToHours(item.target_minutes));
		const workedHours = sortedData.map((item) => this.minutesToHours(item.worked_minutes));
		const projectHours = sortedData.map((item) => this.minutesToHours(item.project_minutes));
		const diffHours = sortedData.map((item) => this.minutesToHours(item.difference_minutes));
		const projectDiffHours = sortedData.map((item) => this.minutesToHours(item.project_diff_minutes));

		const targetBg = this.getCssVariable('--tx-target-time-bg');
		const targetBorder = this.getCssVariable('--tx-target-time-border');

		const differenceBorder = this.getCssVariable('--tx-difference-time-border');

		const workedBg = this.getCssVariable('--tx-worked-time-bg');
		const workedBorder = this.getCssVariable('--tx-worked-time-border');

		const projectDiffBorder = this.getCssVariable('--tx-project-diff-time-border');

		const projectBg = this.getCssVariable('--tx-project-time-bg');
		const projectBorder = this.getCssVariable('--tx-project-time-border');

		const sollLabel = this.translate.instant('txZeitKonto.chartLegendSoll');
		const istLabel = this.translate.instant('txZeitKonto.chartLegendIst');
		const diffLabel = this.translate.instant('txZeitKonto.chartLegendDiff');
		const projectLabel = this.translate.instant('txZeitKonto.chartLegendProject');
		const projectDiffLabel = this.translate.instant('txZeitKonto.chartLegendProjectDiff');

		// Korrekt typisierte Datasets erstellen - nur die grundlegenden Datasets
		const datasets: ChartDataset<'bar' | 'line', number[]>[] = [
			{
				type: 'bar' as const,
				label: sollLabel,
				data: targetHours,
				backgroundColor: targetBg,
				borderColor: targetBorder,
				borderWidth: 1,
				order: 3,
			},
			{
				type: 'bar' as const,
				label: istLabel,
				data: workedHours,
				backgroundColor: workedBg,
				borderColor: workedBorder,
				borderWidth: 1,
				order: 4,
			},
			{
				type: 'line' as const,
				label: diffLabel,
				data: diffHours,
				backgroundColor: differenceBorder,
				borderColor: differenceBorder,
				tension: 0.3,
				borderWidth: 1,
				fill: true,
				order: 1,
			},
		];

		// Projekt-Datasets nur hinzufügen, wenn showProjectChart true ist
		if (this.showProjectChart && this.bde_act) {
			datasets.push({
				type: 'bar' as const,
				label: projectLabel,
				data: projectHours,
				backgroundColor: projectBg,
				borderColor: projectBorder,
				borderWidth: 1,
				order: 5,
			});

			datasets.push({
				type: 'line' as const,
				label: projectDiffLabel,
				data: projectDiffHours,
				backgroundColor: projectDiffBorder,
				borderColor: projectDiffBorder,
				tension: 0.3,
				borderWidth: 1,
				fill: true,
				order: 2,
			});
		}

		// Angepasste Legend-Sortierung basierend auf showProjectChart
		const legendOrderMap: { [key: string]: number } =
			this.showProjectChart && this.bde_act
				? {
						[sollLabel]: 1,
						[diffLabel]: 2,
						[istLabel]: 3,
						[projectDiffLabel]: 4,
						[projectLabel]: 5,
				  }
				: {
						[sollLabel]: 1,
						[diffLabel]: 2,
						[istLabel]: 3,
				  };

		this.chart = new Chart(ctx, {
			type: 'bar',
			data: {
				labels: labels,
				datasets: datasets,
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					y: {
						beginAtZero: true,
						title: { display: true, text: this.translate.instant('txZeitKonto.axisHours') },
						grid: {
							lineWidth: function (context) {
								if (context.tick.value === 0) {
									return 2;
								}
								return 1;
							},
							color: function (context) {
								if (context.tick.value === 0) {
									return 'rgba(0, 0, 0, 0.8)';
								}
								return 'rgba(0, 0, 0, 0.1)';
							},
						},
					},
					x: {
						title: { display: true, text: this.translate.instant('txZeitKonto.axisMonth') },
					},
				},
				plugins: {
					tooltip: {
						callbacks: {
							label: (context) => {
								const label = context.dataset.label || '';
								const value = typeof context.parsed.y === 'number' ? context.parsed.y.toFixed(2) : context.parsed.y;
								return `${label}: ${value}`;
							},
						},
					},
					legend: {
						display: true,
						position: 'top' as const,
						labels: {
							sort: (a, b) => {
								// Verwende die Map für die Sortierung statt benutzerdefinierter Properties
								const labelA = a.text || '';
								const labelB = b.text || '';
								return (legendOrderMap[labelA] || 99) - (legendOrderMap[labelB] || 99);
							},
						},
					},
				},
			},
		});
	}

	private updateChart(): void {
		if (!this.chart) {
			this.createChart();
			return;
		}
		const sortedData = [...this.timeAccountSums].sort((a, b) => a.year * 100 + a.month - (b.year * 100 + b.month));
		const labels = sortedData.map((item) => {
			const monthNames = this.getLocalizedMonthNames();
			return `${monthNames[item.month - 1]} ${item.year}`;
		});
		const targetHours = sortedData.map((item) => this.minutesToHours(item.target_minutes));
		const workedHours = sortedData.map((item) => this.minutesToHours(item.worked_minutes));
		const projectHours = sortedData.map((item) => this.minutesToHours(item.project_minutes));
		const diffHours = sortedData.map((item) => this.minutesToHours(item.difference_minutes));
		const projectDiffHours = sortedData.map((item) => this.minutesToHours(item.project_diff_minutes));

		this.chart.data.labels = labels;

		// Basis-Datensätze aktualisieren (immer vorhanden)
		this.chart.data.datasets[0].data = targetHours; // Soll
		this.chart.data.datasets[1].data = workedHours; // Ist
		this.chart.data.datasets[2].data = diffHours; // Differenz Soll-Ist

		// Projekt-Datensätze nur aktualisieren, wenn sie existieren
		if (this.showProjectChart && this.bde_act && this.chart.data.datasets.length > 3) {
			this.chart.data.datasets[3].data = projectHours; // Auftragszeit
			this.chart.data.datasets[4].data = projectDiffHours; // Differenz Ist-Auftragszeit
		}

		this.chart.update();
	}

	private createAbsenceChart(): void {
		if (!this.absenceChartCanvas) {
			return;
		}
		if (this.absenceChart) {
			this.absenceChart.destroy();
		}
		const ctx = this.absenceChartCanvas.nativeElement.getContext('2d');
		const sortedData = [...this.timeAccountSums].sort((a, b) => a.year * 100 + a.month - (b.year * 100 + b.month));
		const labels = sortedData.map((item) => {
			const monthNames = this.getLocalizedMonthNames();
			return `${monthNames[item.month - 1]} ${item.year}`;
		});

		const absenceTypes = this.absenceTypes;
		// Erstelle Datensätze für jeden Abwesenheitstyp und cast diese als AbsenceChartDataset
		const datasets: AbsenceChartDataset[] = absenceTypes.map((absenceType) => {
			const isSollTage = absenceType.name.includes('Soll-Tage');
			const isIstTage = absenceType.name.includes('Ist-Tage');
			const data = sortedData.map((monthData) => {
				// Add null check for AbsenceList before calling find
				const absenceEntry = monthData.AbsenceList && monthData.AbsenceList.find((abs) => abs.id === absenceType.id);
				return absenceEntry ? absenceEntry.day_count : null;
			});
			return {
				label: absenceType.name,
				data: data,
				backgroundColor: absenceType.color + '80',
				borderColor: absenceType.color,
				borderWidth: 1,
				skipNull: true,
				hidden: isSollTage || isIstTage,
				absenceId: absenceType.id,
			} as AbsenceChartDataset;
		});

		this.absenceChart = new Chart(ctx, {
			type: 'bar',
			data: {
				labels: labels,
				datasets: datasets,
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					y: {
						beginAtZero: true,
						ticks: { stepSize: 1 },
						title: { display: true, text: this.translate.instant('txZeitKonto.axisDays') },
						stacked: false,
					},
					x: {
						title: { display: true, text: this.translate.instant('txZeitKonto.axisMonth') },
						stacked: false,
					},
				},
				plugins: {
					tooltip: {
						callbacks: {
							label: (context) => {
								const label = context.dataset.label || '';
								const value = context.parsed.y;
								const daysText =
									value === 1
										? this.translate.instant('txZeitKonto.day')
										: this.translate.instant('txZeitKonto.days');
								return `${label}: ${value} ${daysText}`;
							},
						},
					},
					legend: {
						display: true,
						position: 'top',
						onClick: function (e, legendItem, legend) {
							Chart.defaults.plugins.legend.onClick.call(this, e, legendItem, legend);
						},
					},
				},
			},
		});
	}

	private updateAbsenceChart(): void {
		if (!this.absenceChart) {
			this.createAbsenceChart();
			return;
		}

		const sortedData = [...this.timeAccountSums].sort((a, b) => a.year * 100 + a.month - (b.year * 100 + b.month));
		this.absenceChart.data.labels = sortedData.map((item) => {
			const monthNames = this.getLocalizedMonthNames();
			return `${monthNames[item.month - 1]} ${item.year}`;
		});
		const absenceTypes = this.absenceTypes;
		while (this.absenceChart.data.datasets.length < absenceTypes.length) {
			const absenceType = absenceTypes[this.absenceChart.data.datasets.length];
			const isSollTage = absenceType.name.includes('Soll-Tage');
			const isIstTage = absenceType.name.includes('Ist-Tage');
			// Füge neuen Datensatz hinzu und cast ihn als AbsenceChartDataset
			(this.absenceChart.data.datasets as AbsenceChartDataset[]).push({
				label: absenceType.name,
				data: [],
				backgroundColor: absenceType.color + '80',
				borderColor: absenceType.color,
				borderWidth: 1,
				hidden: isSollTage || isIstTage,
				absenceId: absenceType.id,
			} as AbsenceChartDataset);
		}
		for (let i = 0; i < absenceTypes.length; i++) {
			const absenceType = absenceTypes[i];
			this.absenceChart.data.datasets[i].data = sortedData.map((monthData) => {
				// Add null check for AbsenceList before calling find
				const absenceEntry = monthData.AbsenceList && monthData.AbsenceList.find((abs) => abs.id === absenceType.id);
				return absenceEntry ? absenceEntry.day_count : 0;
			});
			this.absenceChart.data.datasets[i].label = absenceType.name;
			this.absenceChart.data.datasets[i].backgroundColor = absenceType.color + '80';
			this.absenceChart.data.datasets[i].borderColor = absenceType.color;
			// Verwende Type Assertion, um auf absenceId zuzugreifen
			(this.absenceChart.data.datasets[i] as AbsenceChartDataset).absenceId = absenceType.id;
			const isSollTage = absenceType.name.includes('Soll-Tage');
			const isIstTage = absenceType.name.includes('Ist-Tage');
			this.absenceChart.data.datasets[i].hidden = isSollTage || isIstTage;
		}
		this.absenceChart.update();
	}

	private calculateAbsenceTypes(): AbsenceType[] {
		const absenceTypesMap = new Map<number, AbsenceType>();
		this.timeAccountSums.forEach((monthData) => {
			if (monthData.AbsenceList) {
				// Check if AbsenceList exists
				monthData.AbsenceList.forEach((absence) => {
					if (!absenceTypesMap.has(absence.id)) {
						absenceTypesMap.set(absence.id, {
							id: absence.id,
							name: absence.name,
							totalDays: 0,
							color: absence.color,
						});
					}
					const existingType = absenceTypesMap.get(absence.id);
					if (existingType) {
						existingType.totalDays += absence.day_count;
					}
				});
			}
		});
		return Array.from(absenceTypesMap.values());
	}

	getAbsenceTypes(): AbsenceType[] {
		return this.absenceTypes;
	}

	private minutesToHours(minutes: number): number {
		return parseFloat((minutes / 60).toFixed(2));
	}

	minutesToTimeString(minutes: number): string {
		const hours = Math.floor(Math.abs(minutes) / 60);
		const mins = Math.abs(minutes) % 60;
		const sign = minutes < 0 ? '-' : '';
		return `${sign}${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
	}

	getTotalMinutes(
		type: 'target' | 'worked' | 'project' | 'difference' | 'project_diff',
		numberOfMonths: number = 3
	): number {
		if (!this.timeAccountSums || this.timeAccountSums.length === 0) {
			return 0;
		}
		const sortedData = [...this.timeAccountSums].sort((a, b) => b.year * 100 + b.month - (a.year * 100 + a.month));
		const recentData = sortedData.slice(0, numberOfMonths);
		if (type === 'target') {
			return recentData.reduce((sum, item) => sum + item.target_minutes, 0);
		} else if (type === 'worked') {
			return recentData.reduce((sum, item) => sum + item.worked_minutes, 0);
		} else if (type === 'project') {
			return recentData.reduce((sum, item) => sum + item.project_minutes, 0);
		} else if (type === 'difference') {
			return recentData.reduce((sum, item) => sum + item.difference_minutes, 0);
		} else if (type === 'project_diff') {
			// Berechne project_diff_minutes (worked_minutes - project_minutes)
			return recentData.reduce((sum, item) => sum + (item.worked_minutes - item.project_minutes), 0);
		}
		return 0;
	}

	// Filtert das Zeit-Chart anhand des angeklickten Typs (target, worked, difference, project_diff)
	filterChart(type: 'target' | 'worked' | 'project' | 'difference' | 'project_diff'): void {
		if (!this.chart) {
			return;
		}
		if (this.selectedDataset === type) {
			this.chart.data.datasets.forEach((dataset) => (dataset.hidden = false));
			this.selectedDataset = null;
		} else {
			this.chart.data.datasets.forEach((dataset, index) => {
				// Anpassen der Indizes entsprechend der Anzeige-Reihenfolge
				if (this.showProjectChart && this.bde_act) {
					// Mit Projekt-Daten: Reihenfolge wie vorher
					if (type === 'target' && index === 0) {
						dataset.hidden = false;
					} else if (type === 'worked' && index === 1) {
						dataset.hidden = false;
					} else if (type === 'difference' && index === 2) {
						dataset.hidden = false;
					} else if (type === 'project' && index === 3) {
						dataset.hidden = false;
					} else if (type === 'project_diff' && index === 4) {
						dataset.hidden = false;
					} else {
						dataset.hidden = true;
					}
				} else {
					// Ohne Projekt-Daten: nur die ersten 3 Datasets
					if (type === 'target' && index === 0) {
						dataset.hidden = false;
					} else if (type === 'worked' && index === 1) {
						dataset.hidden = false;
					} else if (type === 'difference' && index === 2) {
						dataset.hidden = false;
					} else {
						dataset.hidden = true;
					}
				}
			});
			this.selectedDataset = type;
		}
		this.chart.update();
	}

	// Filtert das Abwesenheits-Chart anhand der angeklickten Abwesenheits-ID
	filterAbsenceChart(absenceId: number): void {
		if (!this.absenceChart) {
			return;
		}
		if (this.selectedAbsenceDataset === absenceId) {
			this.absenceChart.data.datasets.forEach((dataset) => (dataset.hidden = false));
			this.selectedAbsenceDataset = null;
		} else {
			this.absenceChart.data.datasets.forEach((dataset: ChartDataset<'bar', number[]>) => {
				// Cast zum Zugriff auf unsere zusätzliche Eigenschaft
				if ((dataset as AbsenceChartDataset).absenceId === absenceId) {
					dataset.hidden = false;
				} else {
					dataset.hidden = true;
				}
			});
			this.selectedAbsenceDataset = absenceId;
		}
		this.absenceChart.update();
	}
}
